import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HardDrive, AlertTriangle, Trash2, RefreshCw } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { API, useAuth } from "@/context/AuthContext";

function humanBytes(n) {
  if (n == null || n < 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(0)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

/**
 * Compact live disk-usage pill for the admin nav + analyzer toolbar.
 *
 * - Polls ``/api/admin/disk-usage`` every 60 s (opens the popover
 *   triggers an immediate refresh too).
 * - Amber at ≥ 80 %, red at ≥ 92 %.
 * - Click the pill to open a small popover with the full breakdown
 *   and a "Purge cache now" button which hits
 *   ``/api/admin/disk-usage/purge`` and then re-polls.
 *
 * Non-admins get nothing (endpoint returns 403).
 */
export default function DiskUsageBanner({ compact = false }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [purging, setPurging] = useState(false);

  const poll = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/disk-usage`);
      setData(d);
    } catch {
      /* silently skip — non-admin, offline, etc. */
    }
  }, []);

  useEffect(() => {
    if (!user || user.role !== "admin") return undefined;
    poll();
    const t = setInterval(poll, 60_000);
    return () => clearInterval(t);
  }, [user, poll]);

  if (!data) return null;

  const pct = data.pct_used;
  const critical = data.critical;
  const warn = !critical && data.warn;
  const cls = critical
    ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
    : warn
      ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50";

  const doPurge = async (staleDays = null) => {
    const label = staleDays
      ? `files older than ${staleDays} day${staleDays === 1 ? "" : "s"}`
      : "every cached RAW / preview file";
    if (!window.confirm(
      `Purge ${label}? R2 originals are untouched — they will be re-downloaded on next view.`,
    )) return;
    setPurging(true);
    try {
      const url = staleDays
        ? `${API}/admin/disk-usage/purge?stale_days=${staleDays}`
        : `${API}/admin/disk-usage/purge`;
      const { data: d } = await axios.post(url);
      setData(d);
      toast.success(
        `Purged ${d.purged?.removed || 0} files · freed ${humanBytes(d.purged?.freed_bytes)}`,
      );
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e?.message || "Purge failed",
      );
    } finally {
      setPurging(false);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 h-8 text-[11px] font-semibold cursor-pointer transition-colors ${cls}`}
          data-testid="disk-usage-banner"
          aria-label={`Container disk usage: ${pct}% used`}
        >
          {critical
            ? <AlertTriangle className="h-3.5 w-3.5" />
            : <HardDrive className="h-3.5 w-3.5" />}
          <span className="tabular-nums">{pct}%</span>
          {!compact && (
            <span className="text-slate-500 font-medium hidden md:inline">
              · {humanBytes(data.free_bytes)} free
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-4"
        data-testid="disk-usage-popover"
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-600">
            Container disk
          </p>
          <Button
            size="icon" variant="ghost"
            onClick={poll}
            className="h-7 w-7 text-slate-400 hover:text-slate-800"
            title="Refresh"
            data-testid="disk-usage-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>

        <div className="space-y-2 mb-4">
          <Progress
            value={pct}
            className={`h-2 ${critical ? "bg-red-100 [&>*]:bg-red-500" : warn ? "bg-amber-100 [&>*]:bg-amber-500" : ""}`}
          />
          <p className="text-[13px] font-semibold text-slate-800">
            {humanBytes(data.used_bytes)}
            <span className="text-slate-500 font-medium">
              {" / "}
              {humanBytes(data.total_bytes)}
              {" · "}
              {pct}%
            </span>
          </p>
        </div>

        <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[12px] mb-4">
          <dt className="text-slate-500 font-medium">Free</dt>
          <dd className="text-right font-semibold text-slate-800 tabular-nums">
            {humanBytes(data.free_bytes)}
          </dd>
          <dt className="text-slate-500 font-medium">Cache</dt>
          <dd className="text-right font-semibold text-slate-800 tabular-nums">
            {humanBytes(data.cache_bytes)}
            <span className="text-slate-400 font-normal">
              {" ("}
              {data.cache_pct_of_total}%)
            </span>
          </dd>
          <dt className="text-slate-500 font-medium">Status</dt>
          <dd className={`text-right font-semibold tabular-nums ${critical ? "text-red-600" : warn ? "text-amber-700" : "text-emerald-600"}`}>
            {critical ? "Critical" : warn ? "Warn" : "Healthy"}
          </dd>
        </dl>

        <Button
          onClick={() => doPurge(7)}
          disabled={purging || (data.cache_bytes ?? 0) === 0}
          variant="outline"
          className="w-full border-slate-200 text-slate-700 hover:bg-slate-50 h-9 rounded-md font-semibold mb-2"
          data-testid="disk-usage-purge-stale"
        >
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          {purging ? "Purging…" : "Purge files > 7 days old"}
        </Button>
        <Button
          onClick={() => doPurge(null)}
          disabled={purging || (data.cache_bytes ?? 0) === 0}
          variant="outline"
          className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 h-9 rounded-md font-semibold"
          data-testid="disk-usage-purge"
        >
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          {purging ? "Purging…" : "Purge everything"}
        </Button>
        <p className="text-[11px] text-slate-500 font-medium mt-2 leading-snug">
          Safe — R2 originals stay intact. Auto-janitor already runs hourly
          for orphans + files ≥ 7 days.
        </p>
      </PopoverContent>
    </Popover>
  );
}
